import { useLayoutEffect, useState } from 'react';
import { Tag } from '@/components/Tag';

const TAG_GAP_PX = 4;
const OVERFLOW_COUNT_CLASSES =
    'shrink-0 whitespace-nowrap text-xs font-normal leading-[18px] text-grey-200';

function countTagsFitting(tagWidths: number[], availableWidth: number): number {
    let usedWidth = 0;
    let count = 0;
    for (const tagWidth of tagWidths) {
        const nextUsedWidth =
            count === 0 ? tagWidth : usedWidth + TAG_GAP_PX + tagWidth;
        if (nextUsedWidth > availableWidth) {
            break;
        }
        usedWidth = nextUsedWidth;
        count += 1;
    }
    return count;
}

function countVisibleTags(measurementRow: HTMLDivElement): number {
    const tagWidths = Array.from(measurementRow.children).map(
        (child) => child.getBoundingClientRect().width,
    );
    const overflowCountWidth = tagWidths.pop() ?? 0;
    const availableWidth = measurementRow.getBoundingClientRect().width;
    const countWithoutOverflowCount = countTagsFitting(
        tagWidths,
        availableWidth,
    );
    if (countWithoutOverflowCount === tagWidths.length) {
        return countWithoutOverflowCount;
    }
    return countTagsFitting(
        tagWidths,
        availableWidth - overflowCountWidth - TAG_GAP_PX,
    );
}

interface SingleLineTagRowProps {
    tags: string[];
}

export function SingleLineTagRow({ tags }: SingleLineTagRowProps) {
    const [measurementRow, setMeasurementRow] = useState<HTMLDivElement | null>(
        null,
    );
    const [visibleCount, setVisibleCount] = useState(tags.length);
    const tagsKey = tags.join(',');

    useLayoutEffect(() => {
        if (measurementRow === null) {
            return;
        }
        const remeasure = () =>
            setVisibleCount(countVisibleTags(measurementRow));
        remeasure();
        const resizeObserver = new ResizeObserver(remeasure);
        resizeObserver.observe(measurementRow);
        Array.from(measurementRow.children).forEach((child) =>
            resizeObserver.observe(child),
        );
        return () => resizeObserver.disconnect();
    }, [measurementRow, tagsKey]);

    const hiddenCount = tags.length - visibleCount;

    return (
        <div className="relative h-[22px] w-full overflow-hidden">
            <div
                ref={setMeasurementRow}
                aria-hidden
                className="pointer-events-none invisible absolute inset-x-0 top-0 flex flex-nowrap items-center overflow-hidden"
                style={{ gap: TAG_GAP_PX }}
            >
                {tags.map((tag) => (
                    <Tag key={tag} variant="raised">
                        {tag}
                    </Tag>
                ))}
                <span className={OVERFLOW_COUNT_CLASSES}>
                    {`+${tags.length}`}
                </span>
            </div>
            <div
                className="flex w-full flex-nowrap items-center overflow-hidden"
                style={{ gap: TAG_GAP_PX }}
            >
                {tags.slice(0, visibleCount).map((tag) => (
                    <Tag key={tag} variant="raised">
                        {tag}
                    </Tag>
                ))}
                {hiddenCount > 0 && (
                    <span className={OVERFLOW_COUNT_CLASSES}>
                        {`+${hiddenCount}`}
                    </span>
                )}
            </div>
        </div>
    );
}
